/**
 * Imagery and running mockups.
 *
 * The rule these parts exist to enforce: show the real thing. A capture is a
 * real screen at true scale, a fragment is a real crop, a portrait is a real
 * customer. Where we have no asset yet, we render an honest slot that names
 * what belongs there. Nothing here is decoration.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { ImageIcon, Pause, Play } from 'lucide-react'
import { cn } from '@/utils/cn'
import { CUSTOMER_LOGOS } from './primitives'
import './media.css'

/* ---------------- Hooks ---------------- */

/** True once the element has been on screen. Motion is never spent off screen. */
export function useInView<T extends HTMLElement>(margin = '0px 0px -10% 0px') {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { rootMargin: margin, threshold: 0.25 })
    io.observe(el)
    return () => io.disconnect()
  }, [margin])
  return [ref, inView] as const
}

/**
 * True once the element has been on screen, and only where motion is allowed.
 *
 * The shared way a measurement arrives. A figure counting up, a dial drawing
 * its arc, a bar growing to its value: all the same idea, which is that the
 * number settles in front of you rather than being there before you looked.
 * They run once, they never run again, and under reduced motion this is true
 * from the first render, so the element paints at its final state and no
 * transition has anything to move between.
 *
 * Put the flag on the element as `data-arrived` and let CSS do the rest. That
 * keeps the timing in one place and means a new part gets this by adding one
 * attribute rather than by writing another animation.
 */
export function useArrived<T extends HTMLElement>() {
  const [ref, inView] = useInView<T>()
  const still = usePrefersReducedMotion()
  const [arrived, setArrived] = useState(false)
  useEffect(() => {
    if (inView) setArrived(true)
  }, [inView])
  return [ref, still || arrived] as const
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!mq) return
    setReduced(mq.matches)
    const on = () => setReduced(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return reduced
}

/* ---------------- Captures ---------------- */

interface ShotBase {
  /** Aspect ratio of the frame, e.g. '16 / 10'. Omit when children set the height. */
  ratio?: string
  /** No shadow: the right choice over a photograph or a tint. */
  plain?: boolean
  xl?: boolean
  /** Show the whole image rather than filling the frame. */
  fit?: boolean
  caption?: ReactNode
  className?: string
  style?: CSSProperties
}

/**
 * A real screen. Pass `src` for a supplied capture, or children to compose the
 * frame out of live kit parts, which is what the system prefers: it stays
 * crisp, follows the theme, and cannot go stale.
 */
export function Shot({
  src,
  alt,
  children,
  ratio,
  plain,
  xl,
  fit,
  caption,
  className,
  style,
}: ShotBase & { src?: string; alt?: string; children?: ReactNode }) {
  const frame = (
    <div
      className={cn('ds-shot', xl && 'ds-shot--xl', plain && 'ds-shot--plain', fit && 'ds-shot--fit', className)}
      style={{ aspectRatio: ratio, ...style }}
    >
      {src ? <img src={src} alt={alt ?? ''} loading="lazy" decoding="async" /> : children}
    </div>
  )
  if (!caption) return frame
  return (
    <figure className="ds-figure">
      {frame}
      <figcaption>{caption}</figcaption>
    </figure>
  )
}

/**
 * A muted, looping capture of the product doing something. Plays only while it
 * is on screen, and holds its poster still under reduced motion.
 */
export function Reel({
  src,
  poster,
  label,
  ratio = '16 / 10',
  plain,
  xl,
  caption,
  className,
}: ShotBase & { src: string; poster?: string; label: string }) {
  const [wrapRef, inView] = useInView<HTMLDivElement>()
  const video = useRef<HTMLVideoElement>(null)
  const reduced = usePrefersReducedMotion()
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    const v = video.current
    if (!v) return
    if (inView && !reduced) v.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
    else {
      v.pause()
      setPlaying(false)
    }
  }, [inView, reduced])

  const toggle = () => {
    const v = video.current
    if (!v) return
    if (v.paused) v.play().then(() => setPlaying(true)).catch(() => undefined)
    else {
      v.pause()
      setPlaying(false)
    }
  }

  const frame = (
    <div ref={wrapRef} className={cn('ds-shot', xl && 'ds-shot--xl', plain && 'ds-shot--plain', className)} style={{ aspectRatio: ratio }}>
      <video ref={video} src={src} poster={poster} muted loop playsInline preload="metadata" aria-label={label} />
      <button type="button" className="ds-play ds-play--over" onClick={toggle} aria-pressed={playing}>
        {playing ? <Pause /> : <Play />}
        {playing ? 'Pause' : 'Play'}
      </button>
    </div>
  )
  if (!caption) return frame
  return (
    <figure className="ds-figure">
      {frame}
      <figcaption>{caption}</figcaption>
    </figure>
  )
}

/**
 * What a capture sits on. Quiet paper by default, a wash of the accent, or a
 * photographic ground. The ground is scenery and never a claim: it carries no
 * information, and the card on top takes `plain` so its own edge reads.
 */
export function Stage({
  children,
  tint,
  photo,
  bleed,
  tone = 'dark',
  className,
}: {
  children: ReactNode
  tint?: boolean
  /** Path to the ground image. Wins over `tint` when both are set. */
  photo?: string
  /** Run the ground the full width of the page. Put a container inside. */
  bleed?: boolean
  /** How bright the ground is, which decides the scrim and the chrome on it. */
  tone?: 'dark' | 'light'
  className?: string
}) {
  return (
    <div
      className={cn(
        'ds-stage',
        photo ? 'ds-stage--photo' : tint && 'ds-stage--tint',
        photo && tone === 'light' && 'ds-stage--light',
        bleed && 'ds-stage--bleed',
        className,
      )}
    >
      {photo && <img className="ds-stage__bg" src={photo} alt="" aria-hidden="true" loading="lazy" decoding="async" />}
      {children}
    </div>
  )
}

/* ---------------- Fragments ---------------- */

/**
 * A crop of real UI that runs off the edge of its cell. Inert and hidden from
 * screen readers: the words beside it carry the meaning.
 */
export function Fragment({
  children,
  width,
  x = 22,
  y = 22,
  from = 'left',
  wash,
  className,
}: {
  children: ReactNode
  /** Set only for a crop that should run off the side too. */
  width?: number
  x?: number
  y?: number
  from?: 'left' | 'right'
  wash?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        'ds-fragment',
        width && 'ds-fragment--wide',
        from === 'right' && 'ds-fragment--right',
        wash && 'ds-fragment--wash',
        className,
      )}
      aria-hidden="true"
    >
      <div
        className="ds-fragment__inner"
        style={
          {
            ...(width ? { '--ds-frag-w': `${width}px` } : null),
            '--ds-frag-x': `${x}px`,
            '--ds-frag-y': `${y}px`,
          } as CSSProperties
        }
      >
        {children}
      </div>
    </div>
  )
}

/* ---------------- Ruled cells ---------------- */

export function Cells({
  children,
  cols = 4,
  colsMd = 2,
  colsSm = 1,
  className,
}: {
  children: ReactNode
  cols?: number
  colsMd?: number
  colsSm?: number
  className?: string
}) {
  return (
    <div
      className={cn('ds-cells', className)}
      style={{ '--ds-cols': cols, '--ds-cols-md': colsMd, '--ds-cols-sm': colsSm } as CSSProperties}
    >
      {children}
    </div>
  )
}

export function Cell({ children, className, variant }: { children: ReactNode; className?: string; variant?: 'logo' | 'label' }) {
  return <div className={cn('ds-cell', variant && `ds-cell--${variant}`, className)}>{children}</div>
}

/**
 * A row of customer marks on paper.
 *
 * Was a ruled four-cell grid, which drew a table. Only companies who agreed
 * to be named appear here, and there is no version of this with a logo we
 * invented or a customer we do not have.
 */
export function LogoWall({ label = 'Trusted by teams at' }: { label?: string }) {
  return (
    <div className="ds-wall">
      <p className="ds-wall__label">{label}</p>
      <div className="ds-wall__marks">
        {CUSTOMER_LOGOS.map((l) => (
          <img key={l.alt} src={l.src} alt={l.alt} loading="lazy" />
        ))}
      </div>
    </div>
  )
}

/** A cell of the feature grid: words above, a crop of the real thing below. */
export function Feature({
  title,
  line,
  children,
  wash = true,
  fragmentWidth,
  small,
}: {
  title: string
  line: string
  children: ReactNode
  wash?: boolean
  fragmentWidth?: number
  /** A supporting cell in a row under a lead: shorter, and three to a row. */
  small?: boolean
}) {
  return (
    <div className={cn('ds-feature', small && 'ds-feature--sm')}>
      <div className="ds-feature__text">
        <h3 className="ds-h3">{title}</h3>
        <p>{line}</p>
      </div>
      <Fragment className="ds-feature__media" wash={wash} width={fragmentWidth}>
        {children}
      </Fragment>
    </div>
  )
}

/**
 * The lead of a feature grid: one cell given the whole width of the row,
 * with the words beside the picture instead of above it.
 *
 * A grid of equal cells has no way in. Four of them making four claims at
 * one size reads as a specification table, which is what this replaced. The
 * lead is where the section's strongest claim goes, and it is the only cell
 * whose picture is whole rather than cropped: it holds a component at full
 * size on its own stage, so nothing important runs off an edge.
 */
export function Lead({
  title,
  line,
  children,
  foot,
}: {
  title: string
  line: string
  children: ReactNode
  /** Anything that belongs under the words: a link, a note, a chip. */
  foot?: ReactNode
}) {
  return (
    <div className="ds-feature ds-feature--lead">
      <div className="ds-feature__text">
        <h3 className="ds-h2">{title}</h3>
        <p>{line}</p>
        {foot && <div className="ds-feature__foot">{foot}</div>}
      </div>
      <div className="ds-feature__stage">{children}</div>
    </div>
  )
}

/* ---------------- People ---------------- */

export function Portrait({ src, alt, video, className }: { src?: string; alt?: string; video?: string; className?: string }) {
  const empty = !video && !src
  return (
    <div className={cn('ds-portrait', empty && 'ds-portrait--empty', className)}>
      {video ? (
        <video src={video} muted loop playsInline preload="metadata" poster={src} aria-label={alt} />
      ) : src ? (
        <img src={src} alt={alt ?? ''} loading="lazy" decoding="async" />
      ) : (
        <AssetSlot what="Customer portrait" spec="Real customer, with written permission. 1200 x 1600, natural light." />
      )}
    </div>
  )
}

/**
 * A customer's own words. There is no version of this with an invented quote
 * and no version with a stock face: if we do not have the story, the section
 * does not run.
 */
export function Story({
  quote,
  name,
  role,
  photo,
  sample,
}: {
  quote: string
  name: string
  role: string
  photo?: string
  sample?: string
}) {
  return (
    <div className="ds-story">
      <Portrait className="ds-story__figure" src={photo} alt={`${name}, ${role}`} />
      <div className="ds-story__body">
        <blockquote className="ds-story__quote">{quote}</blockquote>
        <p className="ds-story__who">
          <b>{name}</b>
          {role}
        </p>
        {sample && <p className="ds-sample" style={{ marginTop: 14 }}>{sample}</p>}
      </div>
    </div>
  )
}

/* ---------------- The honest slot ---------------- */

/** What stands in for an asset we do not have yet. It names the asset. */
export function AssetSlot({ what, spec, className }: { what: string; spec: string; className?: string }) {
  return (
    <div className={cn('ds-slot', className)}>
      <ImageIcon aria-hidden="true" />
      <b>{what}</b>
      <span>{spec}</span>
    </div>
  )
}

/* ---------------- Play control, on its own ---------------- */

export function PlayPause({ playing, onClick, over }: { playing: boolean; onClick: () => void; over?: boolean }) {
  return (
    <button type="button" className={cn('ds-play', over && 'ds-play--over')} onClick={onClick} aria-pressed={playing}>
      {playing ? <Pause /> : <Play />}
      {playing ? 'Pause' : 'Play'}
    </button>
  )
}
